import React, { useEffect, useState } from 'react';
import { HiLocationMarker } from '../components/Icons';
import { getAllPromotions, deletePromotion, setPromotionApproval, getCategories, getSocieties, getCustomers, getCustomerSocieties } from '../services/api';
import { Promotion, PromotionCategory, CustomerRecord } from '../services/api';
import PromotionForm, { PromotionFormData } from '../components/PromotionForm';
import PromotionPreview from '../components/PromotionPreview';
import './AdminPromotions.css';

const AdminPromotions: React.FC = () => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [categories, setCategories] = useState<PromotionCategory[]>([]);
  const [societies, setSocieties] = useState<Array<{ id: number; name: string }>>([]);
  const [customers, setCustomers] = useState<CustomerRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
  const [createCustomerId, setCreateCustomerId] = useState<number | null>(null);
  const [selectedCustomerIdForStep, setSelectedCustomerIdForStep] = useState<number | null>(null);
  const [createSocieties, setCreateSocieties] = useState<Array<{ id: number; name: string }>>([]);
  const [createSocietiesLoading, setCreateSocietiesLoading] = useState(false);
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [previewData, setPreviewData] = useState<PromotionFormData | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (createCustomerId != null) {
      setCreateSocietiesLoading(true);
      getCustomerSocieties(createCustomerId)
        .then((r) => setCreateSocieties(r.data))
        .catch(() => setCreateSocieties([]))
        .finally(() => setCreateSocietiesLoading(false));
    } else {
      setCreateSocieties([]);
    }
  }, [createCustomerId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [promotionsRes, categoriesRes, societiesRes, customersRes] = await Promise.all([
        getAllPromotions(),
        getCategories(),
        getSocieties(),
        getCustomers(),
      ]);
      setPromotions(promotionsRes.data);
      setCategories(categoriesRes.data);
      setSocieties(societiesRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this promotion?')) {
      return;
    }

    try {
      await deletePromotion(id);
      setPromotions(promotions.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error deleting promotion:', error);
      alert('Failed to delete promotion');
    }
  };

  const handleEdit = (promotion: Promotion) => {
    setEditingPromotion(promotion);
    setCreateCustomerId(null);
    setShowForm(true);
  };

  const handleAddPromotion = () => {
    setEditingPromotion(null);
    setCreateCustomerId(null);
    setSelectedCustomerIdForStep(null);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingPromotion(null);
    setCreateCustomerId(null);
    setSelectedCustomerIdForStep(null);
    setCreateSocieties([]);
    setPreviewData(null);
    loadData();
  };

  const handleApproval = async (promoId: number, status: 'approved' | 'rejected') => {
    if (approvingId !== null) return;
    setApprovingId(promoId);
    try {
      await setPromotionApproval(promoId, status);
      await loadData();
    } catch (e) {
      console.error('Approval failed', e);
      alert('Failed to update approval status');
    } finally {
      setApprovingId(null);
    }
  };

  const showCustomerStep = showForm && !editingPromotion && createCustomerId == null;
  const showSocietiesLoading = showForm && !editingPromotion && createCustomerId != null && createSocietiesLoading;
  const showPromotionForm = showForm && (editingPromotion != null || (createCustomerId != null && !createSocietiesLoading));
  const formSocieties = editingPromotion ? societies : createSocieties;
  const formCustomerId = editingPromotion ? null : createCustomerId;

  const getTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      story: 'Story',
      spotlight: 'Spotlight',
      category: 'Category',
    };
    return labels[type] || type;
  };

  if (loading) {
    return <div className="loading">Loading promotions...</div>;
  }

  return (
    <div className="admin-promotions">
      <div className="promotions-header">
        <h2>Promotions Management</h2>
        <button onClick={handleAddPromotion} className="add-button">
          + Add Promotion
        </button>
      </div>

      {showSocietiesLoading && (
        <div className="select-customer-step">
          <div className="select-customer-box">
            <p>Loading societies for customer...</p>
          </div>
        </div>
      )}
      {showCustomerStep && (
        <div className="select-customer-step">
          <div className="select-customer-box">
            <h3>Select customer for this promotion</h3>
            <p>Societies will be limited to those enabled for the selected customer.</p>
            <div className="form-group">
              <label>Customer *</label>
              <select
                value={selectedCustomerIdForStep ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedCustomerIdForStep(v ? parseInt(v, 10) : null);
                }}
              >
                <option value="">Choose a customer...</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.customer_name} ({c.email})</option>
                ))}
              </select>
            </div>
            {customers.length === 0 && (
              <p className="hint">No customers yet. Add customers in the Customers section first.</p>
            )}
            <div className="step-actions">
              <button type="button" className="cancel-button" onClick={handleFormClose}>Cancel</button>
              <button
                type="button"
                className="add-button"
                onClick={() => selectedCustomerIdForStep != null && setCreateCustomerId(selectedCustomerIdForStep)}
                disabled={selectedCustomerIdForStep == null}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {showPromotionForm && (
        <div className="create-with-preview">
          <div className="create-form-column">
            <PromotionForm
              promotion={editingPromotion}
              categories={categories}
              societies={formSocieties}
              onClose={handleFormClose}
              onSave={loadData}
              onFormChange={setPreviewData}
              inline
              customerId={formCustomerId}
              showPricingSection
            />
          </div>
          <div className="create-preview-column">
            <PromotionPreview data={previewData} />
          </div>
        </div>
      )}

      <div className="promotions-table-container">
        <table className="promotions-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Title</th>
              <th>Type</th>
              <th>Brand</th>
              <th>Discount</th>
              <th>Coupon Code</th>
              <th>Mobile</th>
              <th>Location</th>
              <th>Pricing</th>
              <th>Status</th>
              <th>Approval</th>
              <th>Valid From</th>
              <th>Valid To</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {promotions.length === 0 ? (
              <tr>
                <td colSpan={14} className="empty-state">
                  No promotions found. Click "Add Promotion" to create one.
                </td>
              </tr>
            ) : (
              promotions.map((promo) => (
                <tr key={promo.id}>
                  <td>{promo.id}</td>
                  <td className="title-cell">{promo.title}</td>
                  <td>
                    <span className={`type-badge type-${promo.promotion_type}`}>
                      {getTypeLabel(promo.promotion_type)}
                    </span>
                  </td>
                  <td>{promo.brand_name || '-'}</td>
                  <td>
                    {promo.discount_percentage
                      ? `${promo.discount_percentage}%`
                      : promo.discount_amount
                      ? `₹${promo.discount_amount}`
                      : '-'}
                  </td>
                  <td>
                    {promo.coupon_code ? (
                      <span className="coupon-code-badge">{promo.coupon_code}</span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    {promo.call_button_enabled === 1 && promo.mobile_number ? (
                      <span className="mobile-badge">{promo.mobile_number}</span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    {promo.show_location === 1 && promo.location_latitude && promo.location_longitude ? (
                      <span className="location-badge" title={`${promo.location_address || ''} (${promo.location_latitude}, ${promo.location_longitude})`}>
                        <HiLocationMarker size={14} /> {promo.location_address || 'Location'}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="pricing-cell">
                    {(promo as Promotion & { stat_prices?: { stat_parameter: string; price_per_unit: number }[] }).stat_prices?.length
                      ? ((promo as Promotion & { stat_prices?: { stat_parameter: string; price_per_unit: number }[] }).stat_prices ?? [])
                          .map((s) => `${s.stat_parameter} ${s.price_per_unit}`)
                          .join(' · ')
                      : '–'}
                  </td>
                  <td>
                    <span className={`status-badge ${promo.is_active === 1 ? 'active' : 'inactive'}`}>
                      {promo.is_active === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    {promo.approval_status === 'pending_approval' ? (
                      <span className="approval-badge pending">Pending</span>
                    ) : promo.approval_status === 'rejected' ? (
                      <span className="approval-badge rejected">Rejected</span>
                    ) : (
                      <span className="approval-badge approved">Approved</span>
                    )}
                    {promo.approval_status === 'pending_approval' && (
                      <div className="approval-actions">
                        <button
                          type="button"
                          className="approve-btn-small"
                          onClick={() => handleApproval(promo.id, 'approved')}
                          disabled={approvingId !== null}
                        >
                          {approvingId === promo.id ? '…' : 'Approve'}
                        </button>
                        <button
                          type="button"
                          className="reject-btn-small"
                          onClick={() => handleApproval(promo.id, 'rejected')}
                          disabled={approvingId !== null}
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </td>
                  <td>{new Date(promo.valid_from).toLocaleDateString()}</td>
                  <td>{new Date(promo.valid_to).toLocaleDateString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button onClick={() => handleEdit(promo)} className="edit-button">
                        Edit
                      </button>
                      <button onClick={() => handleDelete(promo.id)} className="delete-button">
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminPromotions;

